import React, { useState } from 'react';
import { useQuery, useMutation } from '@apollo/client';
import {
  createColumnHelper,
  useReactTable,
  getCoreRowModel,
  getFilteredRowModel,
  FilterFn,
} from '@tanstack/react-table';
import { useAuth } from '../AuthContext';
import {
  ALL_INVENTORY_ITEMS_QUERY,
  ALL_UNITS_QUERY,
  CREATE_INVENTORY_ITEM_MUTATION,
  UPDATE_INVENTORY_ITEM_MUTATION,
} from '../graphql/inventory';
import Table from '../components/Table';
import AddIcon from '@mui/icons-material/Add';
import CloseIcon from '@mui/icons-material/Close';
import InventoryIcon from '@mui/icons-material/Inventory2Outlined';
import './Customers.css';

interface Unit {
  id: number;
  name: string;
  abbreviation: string;
}

interface InventoryItem {
  id: number;
  name: string;
  description: string | null;
  inventoryType: 'FABRIC' | 'HARDWARE' | 'LABOR';
  costCents: number;
  quantityOnHand: number | null;
  widthInches: number | null;
  costLastUpdatedAt: string | null;
  createdAt: string;
  archivedAt: string | null;
  unitByUnitId: Unit;
}

const EMPTY_FORM = {
  name: '',
  inventoryType: 'FABRIC' as 'FABRIC' | 'HARDWARE',
  unitId: '',
  costDollars: '',
  description: '',
  widthInches: '',
  quantityOnHand: '',
};

const filterFn: FilterFn<InventoryItem> = (row, _columnId, filterValue) => {
  if (!filterValue) return true;
  const s = String(filterValue).toLowerCase();
  const item = row.original;
  return (
    item.name.toLowerCase().includes(s) ||
    (item.description || '').toLowerCase().includes(s) ||
    item.inventoryType.toLowerCase().includes(s)
  );
};

const columnHelper = createColumnHelper<InventoryItem>();

function buildColumns(
  onEdit: (item: InventoryItem) => void,
  onArchive: (item: InventoryItem) => void,
  isWorker: boolean,
) {
  return [
    columnHelper.accessor('name', {
      header: 'Name',
      cell: (info) => (
        <span style={{ fontWeight: 600, color: '#111827' }}>{info.getValue()}</span>
      ),
      size: 200,
    }),
    columnHelper.accessor('inventoryType', {
      header: 'Type',
      cell: (info) => {
        const t = info.getValue();
        return (
          <span
            className="status-badge"
            style={{
              backgroundColor: t === 'FABRIC' ? '#ede9fe' : '#dbeafe',
              color: t === 'FABRIC' ? '#6d28d9' : '#1e40af',
              textTransform: 'capitalize',
            }}
          >
            {t.toLowerCase()}
          </span>
        );
      },
      size: 100,
    }),
    columnHelper.accessor((row) => row.unitByUnitId.abbreviation, {
      id: 'unit',
      header: 'Unit',
      cell: (info) => <span style={{ color: '#6b7280' }}>{info.getValue()}</span>,
      size: 70,
    }),
    columnHelper.accessor('costCents', {
      header: 'Cost / Unit',
      cell: (info) => `$${(info.getValue() / 100).toFixed(2)}`,
      size: 110,
    }),
    columnHelper.accessor('quantityOnHand', {
      header: 'Qty on Hand',
      cell: (info) => {
        const qty = info.getValue();
        return qty != null ? (
          <span>{qty} {info.row.original.unitByUnitId.abbreviation}</span>
        ) : '—';
      },
      size: 110,
    }),
    columnHelper.accessor('widthInches', {
      header: 'Width',
      cell: (info) => {
        const w = info.getValue();
        return w != null ? `${w}"` : '—';
      },
      size: 80,
    }),
    columnHelper.accessor('description', {
      header: 'Description',
      cell: (info) => <span style={{ color: '#6b7280' }}>{info.getValue() || '—'}</span>,
      size: 220,
    }),
    ...(!isWorker
      ? [
          columnHelper.display({
            id: 'actions',
            header: '',
            cell: (info) => (
              <div style={{ display: 'flex', gap: 12 }}>
                <button
                  onClick={() => onEdit(info.row.original)}
                  style={{ background: 'none', border: 'none', color: '#3b82f6', cursor: 'pointer', fontSize: 13, textDecoration: 'underline', padding: 0 }}
                >
                  Edit
                </button>
                <button
                  onClick={() => onArchive(info.row.original)}
                  style={{ background: 'none', border: 'none', color: '#6b7280', cursor: 'pointer', fontSize: 13, textDecoration: 'underline', padding: 0 }}
                >
                  {info.row.original.archivedAt ? 'Unarchive' : 'Archive'}
                </button>
              </div>
            ),
            size: 120,
          }),
        ]
      : []),
  ];
}

export default function Inventory() {
  const { user } = useAuth();
  const isWorker = user?.role === 'worker';

  const [items, setItems] = useState<InventoryItem[]>([]);
  const [globalFilter, setGlobalFilter] = useState('');
  const [showArchived, setShowArchived] = useState(false);
  const [showModal, setShowModal] = useState(false);
  const [editingItem, setEditingItem] = useState<InventoryItem | null>(null);
  const [form, setForm] = useState(EMPTY_FORM);
  const [formError, setFormError] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);

  const { loading, refetch } = useQuery(ALL_INVENTORY_ITEMS_QUERY, {
    variables: { domainId: user?.domain_id || 0 },
    skip: !user?.domain_id,
    fetchPolicy: 'network-only',
    onCompleted: (data) => {
      const all: InventoryItem[] = data?.allInventoryItems?.nodes || [];
      setItems(applyFilters(all, showArchived));
    },
  });

  const { data: unitsData } = useQuery(ALL_UNITS_QUERY);
  const units: Unit[] = unitsData?.allUnits?.nodes || [];

  const [createItem] = useMutation(CREATE_INVENTORY_ITEM_MUTATION);
  const [updateItem] = useMutation(UPDATE_INVENTORY_ITEM_MUTATION);

  function applyFilters(all: InventoryItem[], archived: boolean) {
    return all.filter((i) => i.inventoryType !== 'LABOR' && (archived || !i.archivedAt));
  }

  function reloadWith(archived: boolean) {
    refetch().then((res) => {
      const all: InventoryItem[] = res.data?.allInventoryItems?.nodes || [];
      setItems(applyFilters(all, archived));
    });
  }

  const handleArchive = async (item: InventoryItem) => {
    await updateItem({
      variables: {
        id: item.id,
        patch: { archivedAt: item.archivedAt ? null : new Date().toISOString() },
      },
    });
    reloadWith(showArchived);
  };

  const handleOpenCreate = () => {
    setEditingItem(null);
    setForm(EMPTY_FORM);
    setFormError(null);
    setShowModal(true);
  };

  const handleOpenEdit = (item: InventoryItem) => {
    setEditingItem(item);
    setForm({
      name: item.name,
      inventoryType: item.inventoryType as 'FABRIC' | 'HARDWARE',
      unitId: String(item.unitByUnitId.id),
      costDollars: (item.costCents / 100).toFixed(2),
      description: item.description || '',
      widthInches: item.widthInches != null ? String(item.widthInches) : '',
      quantityOnHand: item.quantityOnHand != null ? String(item.quantityOnHand) : '',
    });
    setFormError(null);
    setShowModal(true);
  };

  const closeModal = () => {
    setShowModal(false);
    setEditingItem(null);
    setFormError(null);
  };

  const handleSave = async () => {
    if (!form.name.trim()) { setFormError('Name is required'); return; }
    if (!form.unitId) { setFormError('Unit is required'); return; }
    const costCents = Math.round(parseFloat(form.costDollars || '0') * 100);
    if (isNaN(costCents) || costCents < 0) { setFormError('Enter a valid cost'); return; }

    setSaving(true);
    setFormError(null);
    try {
      const widthInches = form.widthInches ? parseFloat(form.widthInches) : null;
      const quantityOnHand = form.quantityOnHand ? parseFloat(form.quantityOnHand) : null;

      if (editingItem) {
        await updateItem({
          variables: {
            id: editingItem.id,
            patch: {
              name: form.name.trim(),
              inventoryType: form.inventoryType,
              unitId: parseInt(form.unitId, 10),
              costCents,
              description: form.description.trim() || null,
              widthInches,
              quantityOnHand,
              costLastUpdatedAt: new Date().toISOString(),
            },
          },
        });
      } else {
        await createItem({
          variables: {
            domainId: user!.domain_id,
            name: form.name.trim(),
            inventoryType: form.inventoryType,
            unitId: parseInt(form.unitId, 10),
            costCents,
            description: form.description.trim() || null,
            widthInches,
            quantityOnHand,
          },
        });
      }
      closeModal();
      reloadWith(showArchived);
    } catch (err: any) {
      setFormError(err.message || 'Failed to save');
    } finally {
      setSaving(false);
    }
  };

  const columns = buildColumns(handleOpenEdit, handleArchive, isWorker);

  const table = useReactTable({
    data: items,
    columns,
    getCoreRowModel: getCoreRowModel(),
    getFilteredRowModel: getFilteredRowModel(),
    globalFilterFn: filterFn,
    state: { globalFilter },
    onGlobalFilterChange: setGlobalFilter,
  });

  const inputStyle: React.CSSProperties = {
    width: '100%',
    padding: '9px 12px',
    border: '1px solid #d1d5db',
    borderRadius: '6px',
    fontSize: '14px',
    fontFamily: 'inherit',
    boxSizing: 'border-box',
  };

  return (
    <div className="customers-page">
      <div className="page-header">
        <div>
          <h1>Inventory</h1>
          <p className="subtitle">Stocked fabrics and hardware with costs for auto-pricing</p>
        </div>
        <div style={{ display: 'flex', alignItems: 'center', gap: 16 }}>
          <label style={{ fontSize: 13, color: '#6b7280', display: 'flex', alignItems: 'center', gap: 6, cursor: 'pointer' }}>
            <input
              type="checkbox"
              checked={showArchived}
              onChange={(e) => {
                setShowArchived(e.target.checked);
                reloadWith(e.target.checked);
              }}
            />
            Show archived
          </label>
          {!isWorker && (
            <button className="primary-button btn-icon" onClick={handleOpenCreate}>
              <AddIcon fontSize="small" /> New Item
            </button>
          )}
        </div>
      </div>

      <div className="page-content">
        {!loading && items.length === 0 && !globalFilter && (
          <div className="empty-state">
            <div className="empty-icon"><InventoryIcon fontSize="inherit" /></div>
            <h2>No inventory items yet</h2>
            <p>Add stocked fabrics and hardware to auto-fill costs when creating quotes</p>
          </div>
        )}

        {(items.length > 0 || globalFilter) && (
          <>
            <div className="filter-bar">
              <input
                type="text"
                className="filter-input"
                placeholder="Search by name or description..."
                value={globalFilter}
                onChange={(e) => setGlobalFilter(e.target.value)}
              />
            </div>
            <Table table={table} isLoading={loading} />
          </>
        )}
      </div>

      {showModal && (
        <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.5)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000 }}>
          <div style={{ background: 'white', borderRadius: 8, width: 520, maxWidth: '90%', boxShadow: '0 20px 25px rgba(0,0,0,0.15)', maxHeight: '90vh', overflowY: 'auto' }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '20px 24px', borderBottom: '1px solid #e5e7eb', position: 'sticky', top: 0, background: 'white', zIndex: 1 }}>
              <h2 style={{ margin: 0, fontSize: 18, fontWeight: 700 }}>
                {editingItem ? 'Edit Item' : 'New Inventory Item'}
              </h2>
              <button style={{ background: 'none', border: 'none', cursor: 'pointer', color: '#6b7280' }} onClick={closeModal}>
                <CloseIcon fontSize="small" />
              </button>
            </div>

            <div style={{ padding: 24, display: 'flex', flexDirection: 'column', gap: 16 }}>
              {formError && (
                <div style={{ padding: 12, background: '#fee2e2', color: '#dc2626', borderRadius: 6, fontSize: 13 }}>
                  {formError}
                </div>
              )}

              <div>
                <label style={{ display: 'block', fontSize: 13, fontWeight: 600, color: '#374151', marginBottom: 6 }}>
                  Name <span style={{ color: '#dc2626' }}>*</span>
                </label>
                <input
                  type="text"
                  style={inputStyle}
                  placeholder='e.g., "Lining" — must match line item name for auto-pricing'
                  value={form.name}
                  onChange={(e) => setForm({ ...form, name: e.target.value })}
                  disabled={saving}
                />
              </div>

              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 16 }}>
                <div>
                  <label style={{ display: 'block', fontSize: 13, fontWeight: 600, color: '#374151', marginBottom: 6 }}>
                    Type <span style={{ color: '#dc2626' }}>*</span>
                  </label>
                  <select
                    style={inputStyle}
                    value={form.inventoryType}
                    onChange={(e) => setForm({ ...form, inventoryType: e.target.value as 'FABRIC' | 'HARDWARE' })}
                    disabled={saving}
                  >
                    <option value="FABRIC">Fabric</option>
                    <option value="HARDWARE">Hardware</option>
                  </select>
                </div>

                <div>
                  <label style={{ display: 'block', fontSize: 13, fontWeight: 600, color: '#374151', marginBottom: 6 }}>
                    Unit <span style={{ color: '#dc2626' }}>*</span>
                  </label>
                  <select
                    style={inputStyle}
                    value={form.unitId}
                    onChange={(e) => setForm({ ...form, unitId: e.target.value })}
                    disabled={saving}
                  >
                    <option value="">Select unit...</option>
                    {units.map((u) => (
                      <option key={u.id} value={u.id}>{u.name} ({u.abbreviation})</option>
                    ))}
                  </select>
                </div>
              </div>

              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 16 }}>
                <div>
                  <label style={{ display: 'block', fontSize: 13, fontWeight: 600, color: '#374151', marginBottom: 6 }}>
                    Cost per Unit ($) <span style={{ color: '#dc2626' }}>*</span>
                  </label>
                  <input
                    type="number"
                    min="0"
                    step="0.01"
                    style={inputStyle}
                    placeholder="0.00"
                    value={form.costDollars}
                    onChange={(e) => setForm({ ...form, costDollars: e.target.value })}
                    disabled={saving}
                  />
                </div>

                <div>
                  <label style={{ display: 'block', fontSize: 13, fontWeight: 600, color: '#374151', marginBottom: 6 }}>
                    Qty on Hand
                  </label>
                  <input
                    type="number"
                    min="0"
                    step="0.25"
                    style={inputStyle}
                    placeholder="0"
                    value={form.quantityOnHand}
                    onChange={(e) => setForm({ ...form, quantityOnHand: e.target.value })}
                    disabled={saving}
                  />
                </div>
              </div>

              {form.inventoryType === 'FABRIC' && (
                <div>
                  <label style={{ display: 'block', fontSize: 13, fontWeight: 600, color: '#374151', marginBottom: 6 }}>
                    Bolt Width (inches)
                  </label>
                  <input
                    type="number"
                    min="0"
                    step="0.5"
                    style={inputStyle}
                    placeholder='e.g., 54'
                    value={form.widthInches}
                    onChange={(e) => setForm({ ...form, widthInches: e.target.value })}
                    disabled={saving}
                  />
                </div>
              )}

              <div>
                <label style={{ display: 'block', fontSize: 13, fontWeight: 600, color: '#374151', marginBottom: 6 }}>
                  Description
                </label>
                <textarea
                  style={{ ...inputStyle, minHeight: 64, resize: 'vertical' } as React.CSSProperties}
                  placeholder="Optional notes..."
                  value={form.description}
                  onChange={(e) => setForm({ ...form, description: e.target.value })}
                  disabled={saving}
                />
              </div>
            </div>

            <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 12, padding: '16px 24px', borderTop: '1px solid #e5e7eb' }}>
              <button
                style={{ padding: '10px 20px', background: '#f3f4f6', color: '#374151', border: '1px solid #d1d5db', borderRadius: 6, fontWeight: 600, cursor: 'pointer' }}
                onClick={closeModal}
                disabled={saving}
              >
                Cancel
              </button>
              <button
                style={{ padding: '10px 20px', background: '#10b981', color: 'white', border: 'none', borderRadius: 6, fontWeight: 600, cursor: 'pointer' }}
                onClick={handleSave}
                disabled={saving}
              >
                {saving ? 'Saving...' : editingItem ? 'Save Changes' : 'Create Item'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
